'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { createPortal } from 'react-dom';

type SnackbarVariant = 'success' | 'warning' | 'error';

type SnackbarInput = {
  variant?: SnackbarVariant;
  message: string;
  duration?: number;
};

type SnackbarItem = Required<Pick<SnackbarInput, 'variant' | 'message'>> & {
  id: string;
  duration: number;
};

type SnackbarContextValue = {
  snackbar(input: SnackbarInput): void;
};

type SnackbarProps = {
  open: boolean;
  message: string;
  onClose: () => void;
  /** @default 'success' */
  variant?: SnackbarVariant;
  /** @default 5000 */
  duration?: number;
  className?: string;
};

const SnackbarContext = createContext<SnackbarContextValue | null>(null);

const DEFAULT_DURATION = 5000;

const variantStyles: Record<
  SnackbarVariant,
  {
    container: string;
    icon: string;
    Icon: typeof CheckCircleIcon;
    position: 'top' | 'bottom';
  }
> = {
  success: {
    container: 'border-success-dark/25 bg-white',
    icon: 'text-success-dark',
    Icon: CheckCircleIcon,
    position: 'bottom',
  },
  warning: {
    container: 'border-amber-300/40 bg-white',
    icon: 'text-amber-500',
    Icon: ExclamationTriangleIcon,
    position: 'top',
  },
  error: {
    container: 'border-error-dark/25 bg-white',
    icon: 'text-error-dark',
    Icon: XCircleIcon,
    position: 'top',
  },
};

const SnackbarCard = ({
  variant,
  message,
  onDismiss,
  className,
}: {
  variant: SnackbarVariant;
  message: string;
  onDismiss(): void;
  className?: string;
}) => {
  const { container, icon, Icon } = variantStyles[variant];

  return (
    <div
      className={clsx(
        'pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-lg border px-4 py-3 text-left shadow-lg',
        container,
        className
      )}
      role="status"
      aria-atomic="true"
    >
      <Icon className={clsx('mt-0.5 h-6 w-6 shrink-0', icon)} aria-hidden />
      <p className="ut-sm min-w-0 flex-1 break-words text-black-1">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 rounded p-1 text-gray-5 transition hover:bg-neutral-100 hover:text-black-1"
        aria-label="Dismiss notification"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

const TimedSnackbarCard = ({
  item,
  onDismiss,
}: {
  item: SnackbarItem;
  onDismiss(): void;
}) => {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    const id = window.setTimeout(() => {
      onDismissRef.current();
    }, item.duration);
    return () => window.clearTimeout(id);
  }, [item.duration, item.id]);

  return (
    <SnackbarCard
      variant={item.variant}
      message={item.message}
      onDismiss={onDismiss}
    />
  );
};

const stackClassName = (position: 'top' | 'bottom') =>
  position === 'top'
    ? 'pointer-events-none fixed inset-x-4 top-4 z-[100] flex max-w-md flex-col gap-3 sm:left-auto sm:w-full'
    : 'pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-3 p-4 sm:bottom-2';

export const SnackbarProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [mounted, setMounted] = useState(false);
  const [items, setItems] = useState<SnackbarItem[]>([]);
  const idRef = useRef(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  const dismiss = useCallback((id: string) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const snackbar = useCallback((input: SnackbarInput) => {
    idRef.current += 1;
    setItems((current) => [
      ...current,
      {
        id: `snackbar-${idRef.current}`,
        variant: input.variant ?? 'success',
        message: input.message,
        duration: input.duration ?? DEFAULT_DURATION,
      },
    ]);
  }, []);

  const topItems = items.filter(
    (item) => variantStyles[item.variant].position === 'top'
  );
  const bottomItems = items.filter(
    (item) => variantStyles[item.variant].position === 'bottom'
  );

  return (
    <SnackbarContext.Provider value={{ snackbar }}>
      {children}
      {mounted &&
        createPortal(
          <>
            {topItems.length > 0 && (
              <div
                className={stackClassName('top')}
                aria-live="polite"
                aria-relevant="additions"
              >
                {topItems.map((item) => (
                  <TimedSnackbarCard
                    key={item.id}
                    item={item}
                    onDismiss={() => dismiss(item.id)}
                  />
                ))}
              </div>
            )}
            {bottomItems.length > 0 && (
              <div
                className={stackClassName('bottom')}
                aria-live="polite"
                aria-relevant="additions"
              >
                {bottomItems.map((item) => (
                  <TimedSnackbarCard
                    key={item.id}
                    item={item}
                    onDismiss={() => dismiss(item.id)}
                  />
                ))}
              </div>
            )}
          </>,
          document.body
        )}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

/**
 * Controlled single snackbar for local open/close state (e.g. form success).
 * Prefer `useSnackbar()` when a provider is available and stacking is needed.
 */
const Snackbar = ({
  open,
  message,
  onClose,
  variant = 'success',
  duration = DEFAULT_DURATION,
  className,
}: SnackbarProps) => {
  const [mounted, setMounted] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    const id = window.setTimeout(() => {
      onCloseRef.current();
    }, duration);
    return () => window.clearTimeout(id);
  }, [open, duration]);

  if (!mounted || !open) {
    return null;
  }

  const position = variantStyles[variant].position;

  return createPortal(
    <div
      className={stackClassName(position)}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <SnackbarCard
        variant={variant}
        message={message}
        onDismiss={onClose}
        className={className}
      />
    </div>,
    document.body
  );
};

export default Snackbar;
